import {CurrencyAmount, CurrencyCode} from './payroll-currency.model';

type PayrollGuardValue = string | number | boolean | Object | null | undefined;
type PayrollIdentityValue = string | number | null | undefined;
const NUMBER_VALUE_TYPE = 'number';
const STRING_VALUE_TYPE = 'string';
const EMPTY_VALUE_LENGTH = 0;
const MINIMUM_TASK_LINE_COUNT = 0;
const FULL_MONTH_REQUIRED_ISSUE = 'DEAL_FULL_MONTH_REQUIRED';

export type PayrollCalculationMethod = 'INTERNAL' | 'PLANFIX_HOURLY' | 'PLANFIX_DEAL';

export const PAYROLL_CALCULATION_METHOD = {
  INTERNAL: 'INTERNAL' as PayrollCalculationMethod,
  PLANFIX_HOURLY: 'PLANFIX_HOURLY' as PayrollCalculationMethod,
  PLANFIX_DEAL: 'PLANFIX_DEAL' as PayrollCalculationMethod
};

export interface PayrollCalculationMethodSource {
  payrollCalculationMethod?: PayrollCalculationMethod;
  payrollMethod?: boolean;
}

export function resolvePayrollCalculationMethod(source: PayrollCalculationMethodSource): PayrollCalculationMethod {
  if (source && source.payrollCalculationMethod) {
    return source.payrollCalculationMethod;
  }
  return source && source.payrollMethod
    ? PAYROLL_CALCULATION_METHOD.PLANFIX_HOURLY
    : PAYROLL_CALCULATION_METHOD.INTERNAL;
}

export function isPlanfixDealPayroll(source: PayrollCalculationMethodSource): boolean {
  return resolvePayrollCalculationMethod(source) === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL;
}

export type DealPayrollStatus = 'COMPLETE' | 'INCOMPLETE' | 'DISABLED';

export const DEAL_PAYROLL_STATUS = {
  COMPLETE: 'COMPLETE' as DealPayrollStatus,
  INCOMPLETE: 'INCOMPLETE' as DealPayrollStatus,
  DISABLED: 'DISABLED' as DealPayrollStatus
};

export type ManufacturingGrade = 'JUNIOR' | 'MIDDLE';

export const MANUFACTURING_GRADE = {
  JUNIOR: 'JUNIOR' as ManufacturingGrade,
  MIDDLE: 'MIDDLE' as ManufacturingGrade
};

export type ManufacturingDepartment = 'TEST' | 'EQUIPMENT' | 'CHINA';
export type DealPayrollProfile =
  'MANUFACTURING_AREA' | 'CONSTRUCTION_TEAM' | 'FIXED_COEFFICIENT' |
  'PROCUREMENT_CHECKLIST' | 'TASK_GRADE' | 'UNSUPPORTED';
export type DealPayrollCoefficientUnitType = 'AREA' | 'TEAM' | 'TASK_GRADE' | 'FIXED' | 'CHECKLIST';
export type DealPayrollCoefficientMode = 'AREA' | 'TEAM' | 'TASK_GRADE' | 'NONE';

export const DEAL_PAYROLL_COEFFICIENT_MODE = {
  AREA: 'AREA' as DealPayrollCoefficientMode,
  TEAM: 'TEAM' as DealPayrollCoefficientMode,
  TASK_GRADE: 'TASK_GRADE' as DealPayrollCoefficientMode,
  NONE: 'NONE' as DealPayrollCoefficientMode
};

export type DealPayrollClassificationSource =
  'DIRECT_AREA' | 'CONSTRUCTION_QUANTUM_TEAM' | 'CONSTRUCTION_DEFAULT_P' |
  'FIXED_DEPARTMENT' | 'PROCUREMENT_CHECKLIST' | 'REQUIRED_EXPERTISE';
export type DealPayrollChecklistKind = 'KANBAN' | 'STANDARD';
export type DealPayrollTaskRuleCode =
  'TEST_AREA' | 'EQUIPMENT_AREA' | 'CHINA_AREA' | 'CONSTRUCTION_TEAM' |
  'PROCUREMENT_CHECKLIST' | 'DEFAULT_FIXED' | 'TASK_GRADE';
export type DealPayrollEmployeeRoleCode = 'R1' | 'R2' | 'R3' | 'R4' | 'H1' | 'H2' | 'T';
export type DealPayrollGradeCode =
  'JUNIOR_MINUS' | 'JUNIOR' | 'JUNIOR_PLUS' | 'MIDDLE_MINUS' | 'MIDDLE' |
  'MIDDLE_PLUS' | 'SENIOR_MINUS' | 'SENIOR' | 'SENIOR_PLUS';
export type DealPayrollFormulaVersion =
  'PLANNED_PARTICIPATION_V2' | 'ACTUAL_UNITS_V1' | 'ACTUAL_SHARE_WORK_DATE_V3' |
  'ACTUAL_SHARE_PLAN_CAP_V4' | 'PLANNED_ADJUSTED_ACTUAL_V3' | 'MONTHLY_WORK_DATE_V4' | 'MONTHLY_WORK_DATE_V5';

export const DEAL_PAYROLL_FORMULA_VERSION: DealPayrollFormulaVersion = 'MONTHLY_WORK_DATE_V5';
export const DEAL_PAYROLL_CALCULATION_REVISION = '17';
export const DEFAULT_PLANNED_PAYMENT_GRACE_DAYS = 15;
const MAXIMUM_PLANNED_PAYMENT_GRACE_DAYS = 31;
const MINIMUM_PLANNED_PAYMENT_GRACE_DAYS = 0;

export interface DealPayrollMonthlyRates {
  monthlyRateAdjustment?: number;
  effectiveMonthlySalary?: number;
  baseMonthlySalary?: number | null;
  plannedWeight?: number | null;
  actualWeight?: number | null;
  rateMonth?: string | null;
  workingHoursPerMonth?: number | null;
  baseHourRate?: number | null;
  plannedHourRate?: number | null;
  actualHourRate?: number | null;
}

export interface DealPayrollIssue {
  code: string;
  taskId?: number | null;
  blocking: boolean;
}

export interface DealPayrollTaskLine {
  taskId: number;
  taskName: string | null;
  closedAt: string | null;
  plannedAccrualMonth?: string;
  completed?: boolean | null;
  dailyAmounts?: {[date: string]: number};
  dailyTrackedHours?: {[date: string]: number};
  dailyPayableHours?: {[date: string]: number};
  sourceProcessId?: number | null;
  taskGradeFieldId?: number | null;
  taskRuleCode?: DealPayrollTaskRuleCode | null;
  directoryId: number | null;
  areaId: number | null;
  areaName: string | null;
  coefficientUnitType: DealPayrollCoefficientUnitType | null;
  coefficientUnitCode: string | null;
  coefficientUnitName: string | null;
  classificationSource: DealPayrollClassificationSource | null;
  checklistKind: DealPayrollChecklistKind | null;
  checklistRuleHours: number | null;
  employeeActualHours?: number | null;
  adjustedEmployeeActualHours?: number | null;
  analyticEntryCount?: number | null;
  plannedHours: number | null;
  payableBaseHours: number | null;
  totalActualHours: number | null;
  adjustedTotalActualHours?: number | null;
  employeeParticipationPercentage: number | null;
  payableUnits: number | null;
  areaCoefficient: number | null;
  gradeCoefficient: number | null;
  employeeRoleCode: DealPayrollEmployeeRoleCode | null;
  masterCoefficient: number | null;
  plannedAmount?: number | null;
  actualAmount?: number | null;
  ordinaryActualAmount?: number | null;
  businessTripActualAmount?: number | null;
  result: number | null;
  issues: string[];
}

export interface DealPayrollBreakdown extends DealPayrollMonthlyRates {
  businessTripCoefficients?: {[date: string]: number};
  userId: string;
  month: string;
  status: DealPayrollStatus;
  currency: CurrencyCode;
  calculatedAt?: string;
  outputFingerprint?: string;
  calculationRevision?: string;
  provisional: boolean;
  payrollProfile: DealPayrollProfile;
  payrollPositionName: string | null;
  gradeCode: DealPayrollGradeCode | null;
  plannedHourRate?: number | null;
  gradeCoefficient?: number | null;
  actualHourRate: number | null;
  formulaVersion: DealPayrollFormulaVersion | null;
  guaranteedBase?: number | null;
  calculatedTaskAmount?: number | null;
  payableTaskAmount?: number | null;
  preModifierBase?: number | null;
  finalSalary?: number | null;
  trackedActualHours?: number | null;
  analyticEntryCount?: number | null;
  salaryCurrency: CurrencyCode;
  finalSalaryAmounts: CurrencyAmount[];
  issues: DealPayrollIssue[];
  taskLines?: DealPayrollTaskLine[];
  taskLineCount?: number;
  dailyAmounts?: {[date: string]: number};
  dailyTrackedHours?: {[date: string]: number};
  dailyPayableHours?: {[date: string]: number};
}

export function isDealPayrollPayable(status: PayrollGuardValue): boolean {
  return status === DEAL_PAYROLL_STATUS.COMPLETE;
}

export function isFinitePayrollNumber(value: PayrollGuardValue): value is number {
  return typeof value === NUMBER_VALUE_TYPE && isFinite(value as number);
}

export function isDealPayrollIdentityMatch(
  userId: PayrollIdentityValue,
  month: PayrollIdentityValue,
  breakdown: DealPayrollBreakdown
): boolean {
  return hasIdentityValue(userId)
    && hasIdentityValue(month)
    && !!breakdown
    && hasIdentityValue(breakdown.userId)
    && hasIdentityValue(breakdown.month)
    && String(userId) === String(breakdown.userId)
    && String(month) === String(breakdown.month);
}

export function isDealPayrollPeriodDiagnostic(userId: PayrollIdentityValue, breakdown: DealPayrollBreakdown): boolean {
  return hasIdentityValue(userId) && !!breakdown && hasIdentityValue(breakdown.userId)
    && String(userId) === String(breakdown.userId) && breakdown.month === null
    && breakdown.status === DEAL_PAYROLL_STATUS.INCOMPLETE && Array.isArray(breakdown.issues)
    && breakdown.issues.some(issue => !!issue && issue.blocking === true && issue.code === FULL_MONTH_REQUIRED_ISSUE);
}

export function hasExactDealPayrollMemoryMatch(
  reference: DealPayrollBreakdown,
  candidate: DealPayrollBreakdown
): boolean {
  if (!hasExactDealPayrollMetadata(reference, candidate)) {
    return false;
  }
  const referenceCount = exactTaskLineCount(reference.taskLineCount);
  const candidateCount = exactTaskLineCount(candidate.taskLineCount);
  return referenceCount !== null
    && candidateCount !== null
    && referenceCount === candidateCount
    && !!candidate.taskLines
    && candidate.taskLines.length === candidateCount;
}

function hasExactDealPayrollMetadata(
  reference: DealPayrollBreakdown,
  candidate: DealPayrollBreakdown
): boolean {
  return !!reference && !!candidate
    && reference.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION
    && candidate.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION
    && isDealPayrollIdentityMatch(reference.userId, reference.month, candidate)
    && hasNonEmptyString(reference.calculatedAt)
    && hasNonEmptyString(candidate.calculatedAt)
    && hasMatchingDealPayrollCalculation(reference, candidate)
    && hasNonEmptyString(reference.outputFingerprint)
    && hasNonEmptyString(candidate.outputFingerprint)
    && reference.outputFingerprint === candidate.outputFingerprint
    && reference.status === candidate.status;
}

function hasMatchingDealPayrollCalculation(reference: DealPayrollBreakdown, candidate: DealPayrollBreakdown): boolean {
  if (reference.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION
    || candidate.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION) {
    return reference.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION
      && candidate.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION;
  }
  return reference.calculatedAt === candidate.calculatedAt;
}

export function isValidPlannedPaymentGraceDays(value: PayrollGuardValue): value is number {
  return isFinitePayrollNumber(value) && value >= MINIMUM_PLANNED_PAYMENT_GRACE_DAYS
    && value <= MAXIMUM_PLANNED_PAYMENT_GRACE_DAYS && Math.floor(value) === value;
}

function exactTaskLineCount(value: number): number | null {
  return isFinitePayrollNumber(value) && value >= MINIMUM_TASK_LINE_COUNT && Math.floor(value) === value
    ? value
    : null;
}

function hasIdentityValue(value: PayrollIdentityValue): boolean {
  return value !== null && value !== undefined && String(value).trim().length > EMPTY_VALUE_LENGTH;
}

function hasNonEmptyString(value: string): boolean {
  return typeof value === STRING_VALUE_TYPE && value.trim().length > EMPTY_VALUE_LENGTH;
}

export interface UserPayrollSettingsRequest {
  confirmGradeAssignment?: boolean;
  expectedUserRevision?: number | null;
  payrollCalculationMethod: PayrollCalculationMethod;
  manufacturingGrade?: ManufacturingGrade | null;
  gradeCode?: DealPayrollGradeCode | null;
  assignmentRevision?: number | null;
}

export interface DealPayrollProbation {
  userId: string;
  approved: boolean;
  eligibleAt: string | null;
  requiredTrainingCount: number;
  completedTrainingCount: number;
  blockers: string[];
  canApprove: boolean;
}

export interface UserPayrollSettingsResumeRequest {
  expectedUserRevision: number;
  operationToken: string;
}

export interface UserPayrollSettingsResponse extends PayrollCalculationMethodSource, DealPayrollMonthlyRates {
  canEnableDeal?: boolean;
  dealEnableBlockers?: string[];
  gradeGrowthBlockers?: string[];
  manualGradeCode?: DealPayrollGradeCode | null;
  effectiveGradeCode?: DealPayrollGradeCode | null;
  effectiveGradeSource?: 'PF_BALANCE' | 'PF_GRADE_ANCHOR' | 'MANUAL' | 'MINIMUM';
  effectiveGradeApplicationStatus?: string;
  userRevision?: number | null;
  pendingOperationToken?: string | null;
  internalSharePercent?: number;
  userId: string;
  payrollCalculationMethod: PayrollCalculationMethod;
  payrollMethod: boolean;
  manufacturingGrade?: ManufacturingGrade | null;
  positionOwnerGroupId: string | null;
  positionId: string | null;
  gradeCode: DealPayrollGradeCode | null;
  assignmentRevision: number | null;
  gradeLocked?: boolean;
  gradeProvisional?: boolean;
  probation?: DealPayrollProbation | null;
  assignmentEligibility?: DealPayrollAssignmentEligibility | null;
  payrollPositions: DealPayrollPosition[];
  grades: DealPayrollGrade[];
  eligible: boolean;
  canManage: boolean;
  blockers: string[];
  payrollCurrency?: CurrencyCode;
}

export interface DealPayrollAssignmentEligibility {
  canAssign: boolean;
  blockers: string[];
  requiredTrainingCount: number;
  completedTrainingCount: number;
  trainings: DealPayrollAssignmentTraining[];
}

export interface DealPayrollAssignmentTraining {
  currentHeadRequired?: boolean;
  specializationId: string;
  name: string;
  requiredLevel: number;
  currentLevel: number;
  companyRequired: boolean;
  departmentSources: Array<{id: string; name: string}>;
}

export interface DealPayrollArea {
  department: ManufacturingDepartment | string;
  directoryId: number;
  areaId: number;
  displayName: string;
  coefficient: number | null;
  confirmed: boolean;
}

export interface DealPayrollCoefficientUnit {
  type: DealPayrollCoefficientUnitType;
  code: string;
  displayName: string;
  coefficient: number | null;
  confirmed: boolean;
  editable: boolean;
}

export interface DealPayrollPosition {
  ownerGroupId: string | null;
  ownerGroupName: string | null;
  positionId: string | null;
  displayName: string;
  plannedHourRate?: number | null;
  actualHourRate: number | null;
  rateCurrency?: CurrencyCode;
  active: boolean | null;
}

export interface DealPayrollGrade {
  gradeCode: DealPayrollGradeCode;
  enabled: boolean | null;
  inUse?: boolean;
  minPoints: number | null;
  coefficient: number | null;
  monthlySalary?: number | null;
  plannedWeight?: number | null;
  baseHourRate?: number | null;
  plannedHourRate?: number | null;
  actualHourRate?: number | null;
}

export type GrowthMode = 'PLANFIX_AND_LEARNING' | 'LEARNING_ONLY';
export const GROWTH_MODE = {
  PLANFIX_AND_LEARNING: 'PLANFIX_AND_LEARNING' as GrowthMode,
  LEARNING_ONLY: 'LEARNING_ONLY' as GrowthMode
};
export type GrowthProfessionalProfile = 'DESIGN_ENGINEER' | 'SOFTWARE_DEVELOPER' | 'HR_DEPARTMENT';
export const GROWTH_PROFESSIONAL_PROFILES: GrowthProfessionalProfile[] = ['DESIGN_ENGINEER', 'SOFTWARE_DEVELOPER', 'HR_DEPARTMENT'];

export interface DealPayrollGroupSettings extends DealPayrollMonthlyRates {
  businessTripCoefficient?: number | null;
  parentDepartmentId?: string | null;
  growthMode?: GrowthMode | null;
  growthProfessionalProfile?: GrowthProfessionalProfile | null;
  effectiveGrowthProfessionalProfile?: GrowthProfessionalProfile | null;
  plannedPaymentGraceDays?: number | null;
  groupId: string;
  groupName: string | null;
  policyGroupId?: string | null;
  policyGroupName?: string | null;
  effectiveFromMonth?: string | null;
  payrollCurrency?: CurrencyCode;
  rateConversions?: {[source: string]: {[target: string]: number}};
  department?: ManufacturingDepartment | string | null;
  payrollPositions: DealPayrollPosition[];
  grades: DealPayrollGrade[];
  areas: DealPayrollArea[];
  coefficientMode: DealPayrollCoefficientMode;
  coefficientUnits: DealPayrollCoefficientUnit[];
  masterCoefficient: number | null;
  revision: number | null;
  updatedAt?: string;
  updatedBy?: string;
  eligible: boolean;
  canManage: boolean;
  calculationEnabled: boolean;
  restConfigured: boolean;
  areaCatalogAvailable?: boolean;
  blockers: string[];
}

export interface DealPayrollGroupSettingsRequest {
  businessTripCoefficient?: number | null;
  growthMode?: GrowthMode;
  growthProfessionalProfile?: GrowthProfessionalProfile | null;
  policyGroupId?: string | null;
  plannedPaymentGraceDays?: number;
  revision: number | null;
  baseMonthlySalary: number | null;
  plannedWeight: number | null;
  grades: DealPayrollGrade[];
  areas?: DealPayrollArea[];
  coefficientUnits?: DealPayrollCoefficientUnit[];
  masterCoefficient?: number | null;
}

export interface DealPayrollRates {
  actualHourRate: number | null;
  juniorCoefficient: number | null;
  middleCoefficient: number | null;
  internalGroupIds: {[department: string]: string};
  revision: number | null;
  updatedAt?: string;
  updatedBy?: string;
}

export interface DealPayrollRatesRequest {
  actualHourRate: number;
  juniorCoefficient: number;
  middleCoefficient: number;
  internalGroupIds: {[department: string]: string};
  revision: number;
}

export function buildDealPayrollGroupSettingsRequest(
  settings: DealPayrollGroupSettings
): DealPayrollGroupSettingsRequest {
  const request: DealPayrollGroupSettingsRequest = {
    policyGroupId: settings.policyGroupId || settings.groupId,
    revision: settings.revision,
    baseMonthlySalary: settings.baseMonthlySalary,
    plannedWeight: settings.plannedWeight,
    grades: (settings.grades || []).map((grade: DealPayrollGrade) => ({
      gradeCode: grade.gradeCode, enabled: grade.enabled, minPoints: grade.minPoints,
      coefficient: grade.coefficient, plannedWeight: grade.plannedWeight
    }))
  };
  if (settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.AREA) {
    request.areas = (settings.areas || []).slice();
  }
  appendOptionalSettings(request, settings);
  return request;
}

function appendOptionalSettings(request: DealPayrollGroupSettingsRequest, settings: DealPayrollGroupSettings): void {
  if (settings.businessTripCoefficient !== undefined) { request.businessTripCoefficient = settings.businessTripCoefficient; }
  if (settings.growthMode != null) { request.growthMode = settings.growthMode; }
  if (settings.growthProfessionalProfile !== undefined) { request.growthProfessionalProfile = settings.growthProfessionalProfile; }
  if (isValidPlannedPaymentGraceDays(settings.plannedPaymentGraceDays)) { request.plannedPaymentGraceDays = settings.plannedPaymentGraceDays; }
  if (settings.coefficientMode !== DEAL_PAYROLL_COEFFICIENT_MODE.TEAM
    && settings.coefficientMode !== DEAL_PAYROLL_COEFFICIENT_MODE.TASK_GRADE) { return; }
  request.coefficientUnits = (settings.coefficientUnits || [])
    .filter(unit => !!unit && unit.type === settings.coefficientMode && unit.editable === true)
    .map(unit => Object.assign({}, unit));
  if (settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.TEAM) { request.masterCoefficient = settings.masterCoefficient; }
}

export interface DealPayrollErrorResponse {
  code: string;
  message: string;
  blockers: string[];
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/models/deal-payroll.model.ts